import type { IDBPDatabase} from 'idb';
import { deleteDB, openDB } from 'idb';
import ImageItem from '../Models/ImageItemModel';
import CommentItem from '../Models/CommentItemModel';
import Country from '../Models/CountryModel';
import User from '../Models/UserModel';
import IndexedDBStore from '../Enums/IndexedDBStore';
import ChecklistModel from '../Models/Checklists/ChecklistModelModel';
import FilledChecklist from '../Models/Checklists/FilledChecklistModel';
import BugReport from '../Models/BugReportModel';
import InspectingCompany from '../Models/InspectingCompanyModel';
import Inspector from '../Models/InspectorModel';
import Department from '../Models/DepartmentModel';
import UserDepartmentLink from '../Models/UserDepartmentLinkModel';
import Category from '../Models/CategoryModel';
import DocumentTreeNode from '../Models/DocumentTreeNodeModel';
import DocumentNode from '../Models/DocumentNodeModel';
import { safeParseNullDefault, removeNulls } from 'shouldve-been-js';
import MemoTemplate from '../Models/MemoTemplateModel';
import Folder from '../Models/FolderModel';
import ObservationModel from '../Models/ObservationModelModel';
import FilledObservation from '../Models/FilledObservationModel';
import ObservationChapter from '../Models/ObservationChapterModel';
import ObservationSubChapter from '../Models/ObservationSubChapterModel';
import ObservationRuleSet from '../Models/ObservationRuleSetModel';

class IndexedDBService {

    private _id: string;

    public constructor(id: string) {
        this._id = id;
    }

    public get id(): string {
        return this._id;
    }

    public set id(value: string) {
        this._id = value;
    }

    public static isIndexedDBAvailable(): boolean {
        return ('indexedDB' in window);
    }

    //see https://web.dev/indexeddb/ for how to properly do migrations in the future
    private async openDatabase(): Promise<IDBPDatabase> {

        return openDB(this.id, 1, {
            upgrade(db) {
                db.createObjectStore(IndexedDBStore.LOGO_BLOB);
                db.createObjectStore(IndexedDBStore.IMAGE_ITEM, {keyPath: '_imageItemId'});
                db.createObjectStore(IndexedDBStore.COMMENT_ITEM, {keyPath: '_commentItemId'});
                db.createObjectStore(IndexedDBStore.COUNTRY, {keyPath: '_code'});
                db.createObjectStore(IndexedDBStore.USER, {keyPath: '_userId'});
                db.createObjectStore(IndexedDBStore.CHECKLIST_MODEL, {keyPath: '_checklistId'});
                db.createObjectStore(IndexedDBStore.FILLED_CHECKLIST, {keyPath: '_filledChecklistId'});
                db.createObjectStore(IndexedDBStore.BUG_REPORT, {keyPath: '_bugReportId'});
                db.createObjectStore(IndexedDBStore.INSPECTING_COMPANY, {keyPath: '_companyId'});
                db.createObjectStore(IndexedDBStore.INSPECTOR, {keyPath: '_inspectorId'});
                db.createObjectStore(IndexedDBStore.DEPARTMENT, {keyPath: '_departmentId'});
                db.createObjectStore(IndexedDBStore.USER_DEPARTMENT_LINK, {keyPath: '_userId'});
                db.createObjectStore(IndexedDBStore.CATEGORY, {keyPath: '_categoryId'});
                db.createObjectStore(IndexedDBStore.DOCUMENT_TREE_NODE, {keyPath: '_nodeId'});
                db.createObjectStore(IndexedDBStore.DOCUMENT_NODE, {keyPath: '_documentId'});
                db.createObjectStore(IndexedDBStore.DOCUMENT, {keyPath: '_documentId'});
                db.createObjectStore(IndexedDBStore.MEMO_TEMPLATE, {keyPath: '_templateId'});
                db.createObjectStore(IndexedDBStore.FOLDER, {keyPath: '_folderId'});
                db.createObjectStore(IndexedDBStore.OBSERVATION_MODEL, {keyPath: '_observationId'});
                db.createObjectStore(IndexedDBStore.FILLED_OBSERVATION, {keyPath: '_filledObservationId'});
                db.createObjectStore(IndexedDBStore.OBSERVATION_CHAPTER, {keyPath: '_chapterId'});
                db.createObjectStore(IndexedDBStore.OBSERVATION_SUBCHAPTER, {keyPath: '_subChapterId'});
                db.createObjectStore(IndexedDBStore.OBSERVATION_RULESET, {keyPath: '_ruleSetId'});
            },
        });
    }

    public async deleteDatabase(): Promise<boolean> {

        try {
            await deleteDB(this.id);
            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    //logoblob----------------------------------------------------------------------------------------------------

    public async getLogoBlob(): Promise<Blob | null> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                const logoBlob: Blob | null = safeParseNullDefault<Blob>(await db.get(IndexedDBStore.LOGO_BLOB, '1'), new Blob([]));
                
                return logoBlob;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return null;
        }
    }

    public async setLogoBlob(logoBlob: Blob | null): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();
    
            try {
                await db.put(IndexedDBStore.LOGO_BLOB, logoBlob, '1');
                
                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async clearLogoBlob(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();
            
            try {
                await db.delete(IndexedDBStore.LOGO_BLOB, '1');

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    //image items----------------------------------------------------------------------------------------------------

    public async getAllImageItems(): Promise<ImageItem[]> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const imageItemObjects: any = await db.getAll(IndexedDBStore.IMAGE_ITEM);
                
                if (!Array.isArray(imageItemObjects)) {
                    return [];
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const imageItems: (ImageItem | null)[] = imageItemObjects.map((imageItemObject: any) => ImageItem.fromIndexedDB(imageItemObject));

                return removeNulls(imageItems);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return [];
        }
    }

    public async getImageItem(imageItemId: string): Promise<ImageItem | null> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
                const imageItemObject: any = await db.get(IndexedDBStore.IMAGE_ITEM, imageItemId);

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return ImageItem.fromIndexedDB(imageItemObject);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return null;
        }
    }

    public async insertImageItem(imageItem: ImageItem): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.add(IndexedDBStore.IMAGE_ITEM, imageItem);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async updateImageItem(imageItem: ImageItem): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.put(IndexedDBStore.IMAGE_ITEM, imageItem);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeImageItem(imageItemId: string): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.delete(IndexedDBStore.IMAGE_ITEM, imageItemId);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeAllImageItems(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.IMAGE_ITEM);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    //comment items----------------------------------------------------------------------------------------------------

    public async getAllCommentItems(): Promise<CommentItem[]> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const commentItemObjects: any = await db.getAll(IndexedDBStore.COMMENT_ITEM);
                
                if (!Array.isArray(commentItemObjects)) {
                    return [];
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const commentItems: (CommentItem | null)[] = commentItemObjects.map((commentItemObject: any) => CommentItem.fromIndexedDB(commentItemObject));

                return removeNulls(commentItems);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return [];
        }
    }

    public async getCommentItem(commentItemId: string): Promise<CommentItem | null> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
                const commentItemObject: any = await db.get(IndexedDBStore.COMMENT_ITEM, commentItemId);

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return CommentItem.fromIndexedDB(commentItemObject);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return null;
        }
    }

    public async insertCommentItem(commentItem: CommentItem): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.add(IndexedDBStore.COMMENT_ITEM, commentItem);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async updateCommentItem(commentItem: CommentItem): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.put(IndexedDBStore.COMMENT_ITEM, commentItem);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeCommentItem(commentItemId: string): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.delete(IndexedDBStore.COMMENT_ITEM, commentItemId);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeAllCommentItems(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.COMMENT_ITEM);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    //countries----------------------------------------------------------------------------------------------------

    public async getAllCountries(): Promise<Country[]> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const countryObjects: any = await db.getAll(IndexedDBStore.COUNTRY);
                
                if (!Array.isArray(countryObjects)) {
                    return [];
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const countries: (Country | null)[] = countryObjects.map((countryObject: any) => Country.fromIndexedDB(countryObject));

                return removeNulls(countries);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return [];
        }
    }

    public async replaceAllCountries(countries: Country[]): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.COUNTRY);

                for (let i: number = 0; i < countries.length; i++) {
                    // eslint-disable-next-line no-await-in-loop
                    await db.add(IndexedDBStore.COUNTRY, countries[i]);
                }

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeAllCountries(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.COUNTRY);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    //users----------------------------------------------------------------------------------------------------

    public async getAllUsers(): Promise<User[]> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const userObjects: any = await db.getAll(IndexedDBStore.USER);
                
                if (!Array.isArray(userObjects)) {
                    return [];
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const users: (User | null)[] = userObjects.map((userObject: any) => User.fromIndexedDB(userObject));

                return removeNulls(users);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return [];
        }
    }

    public async replaceAllUsers(users: User[]): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.USER);

                for (let i: number = 0; i < users.length; i++) {
                    // eslint-disable-next-line no-await-in-loop
                    await db.add(IndexedDBStore.USER, users[i]);
                }

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeAllUsers(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.USER);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    //checklistmodels----------------------------------------------------------------------------------------------------

    public async getAllChecklistModels(): Promise<ChecklistModel[]> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const checklistModelObjects: any = await db.getAll(IndexedDBStore.CHECKLIST_MODEL);
                
                if (!Array.isArray(checklistModelObjects)) {
                    return [];
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const checklistModels: (ChecklistModel | null)[] = checklistModelObjects.map((checklistModelObject: any) => ChecklistModel.fromIndexedDB(checklistModelObject));

                return removeNulls(checklistModels);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return [];
        }
    }

    public async getChecklistModel(checklistId: string): Promise<ChecklistModel | null> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
                const checklistModelObject: any = await db.get(IndexedDBStore.CHECKLIST_MODEL, checklistId);

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return ChecklistModel.fromIndexedDB(checklistModelObject);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return null;
        }
    }

    public async replaceAllChecklistModels(checklistModels: ChecklistModel[]): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.CHECKLIST_MODEL);

                for (let i: number = 0; i < checklistModels.length; i++) {
                    // eslint-disable-next-line no-await-in-loop
                    await db.add(IndexedDBStore.CHECKLIST_MODEL, checklistModels[i]);
                }

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeAllChecklistModels(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.CHECKLIST_MODEL);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    //filled checklists----------------------------------------------------------------------------------------------------

    public async getAllFilledChecklists(): Promise<FilledChecklist[]> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const filledChecklistObjects: any = await db.getAll(IndexedDBStore.FILLED_CHECKLIST);
                
                if (!Array.isArray(filledChecklistObjects)) {
                    return [];
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const filledChecklists: (FilledChecklist | null)[] = filledChecklistObjects.map((filledChecklistObject: any) => FilledChecklist.fromIndexedDB(filledChecklistObject));

                return removeNulls(filledChecklists);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return [];
        }
    }

    public async getFilledChecklist(filledChecklistId: string): Promise<FilledChecklist | null> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
                const filledChecklistObject: any = await db.get(IndexedDBStore.FILLED_CHECKLIST, filledChecklistId);

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return FilledChecklist.fromIndexedDB(filledChecklistObject);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return null;
        }
    }

    public async insertFilledChecklist(filledChecklist: FilledChecklist): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.add(IndexedDBStore.FILLED_CHECKLIST, filledChecklist);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async updateFilledChecklist(filledChecklist: FilledChecklist): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.put(IndexedDBStore.FILLED_CHECKLIST, filledChecklist);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeFilledChecklist(filledChecklistId: string): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.delete(IndexedDBStore.FILLED_CHECKLIST, filledChecklistId);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeAllFilledChecklists(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.FILLED_CHECKLIST);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    //bug report----------------------------------------------------------------------------------------------------

    public async getAllBugReports(): Promise<BugReport[]> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const bugReportObjects: any = await db.getAll(IndexedDBStore.BUG_REPORT);
                
                if (!Array.isArray(bugReportObjects)) {
                    return [];
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const bugReports: (BugReport | null)[] = bugReportObjects.map((bugReportObject: any) => BugReport.fromIndexedDB(bugReportObject));

                return removeNulls(bugReports);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return [];
        }
    }

    public async insertBugReport(bugReport: BugReport): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.add(IndexedDBStore.BUG_REPORT, bugReport);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeAllBugReports(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.BUG_REPORT);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    //inspecting companies----------------------------------------------------------------------------------------------------

    public async getAllInspectingCompanies(): Promise<InspectingCompany[]> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const inspectingCompanyObjects: any = await db.getAll(IndexedDBStore.INSPECTING_COMPANY);
                
                if (!Array.isArray(inspectingCompanyObjects)) {
                    return [];
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const inspectingCompanies: (InspectingCompany | null)[] = inspectingCompanyObjects.map((inspectingCompanyObject: any) => InspectingCompany.fromIndexedDB(inspectingCompanyObject));

                return removeNulls(inspectingCompanies);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return [];
        }
    }

    public async replaceAllInspectingCompanies(inspectingCompanies: InspectingCompany[]): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.INSPECTING_COMPANY);

                for (let i: number = 0; i < inspectingCompanies.length; i++) {
                    // eslint-disable-next-line no-await-in-loop
                    await db.add(IndexedDBStore.INSPECTING_COMPANY, inspectingCompanies[i]);
                }

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeAllInspectingCompanies(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.INSPECTING_COMPANY);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    //inspectors----------------------------------------------------------------------------------------------------

    public async getAllInspectors(): Promise<Inspector[]> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const inspectorObjects: any = await db.getAll(IndexedDBStore.INSPECTOR);
                
                if (!Array.isArray(inspectorObjects)) {
                    return [];
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const inspectors: (Inspector | null)[] = inspectorObjects.map((inspectorObject: any) => Inspector.fromIndexedDB(inspectorObject));

                return removeNulls(inspectors);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return [];
        }
    }

    public async replaceAllInspectors(inspectors: Inspector[]): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.INSPECTOR);

                for (let i: number = 0; i < inspectors.length; i++) {
                    // eslint-disable-next-line no-await-in-loop
                    await db.add(IndexedDBStore.INSPECTOR, inspectors[i]);
                }

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeAllInspectors(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.INSPECTOR);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    //departments----------------------------------------------------------------------------------------------------

    public async getAllDepartments(): Promise<Department[]> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const departmentObjects: any = await db.getAll(IndexedDBStore.DEPARTMENT);
                
                if (!Array.isArray(departmentObjects)) {
                    return [];
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const departments: (Department | null)[] = departmentObjects.map((departmentObject: any) => Department.fromIndexedDB(departmentObject));

                return removeNulls(departments);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return [];
        }
    }

    public async replaceAllDepartments(departments: Department[]): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.DEPARTMENT);

                for (let i: number = 0; i < departments.length; i++) {
                    // eslint-disable-next-line no-await-in-loop
                    await db.add(IndexedDBStore.DEPARTMENT, departments[i]);
                }

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeAllDepartments(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.DEPARTMENT);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    //user department links----------------------------------------------------------------------------------------------------

    public async getAllUserDepartmentLinks(): Promise<UserDepartmentLink[]> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const userDepartmentLinkObjects: any = await db.getAll(IndexedDBStore.USER_DEPARTMENT_LINK);
                
                if (!Array.isArray(userDepartmentLinkObjects)) {
                    return [];
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const userDepartmentLinks: (UserDepartmentLink | null)[] = userDepartmentLinkObjects.map((userDepartmentLinkObject: any) => UserDepartmentLink.fromIndexedDB(userDepartmentLinkObject));

                return removeNulls(userDepartmentLinks);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return [];
        }
    }

    public async replaceAllUserDepartmentLinks(userDepartmentLinks: UserDepartmentLink[]): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.USER_DEPARTMENT_LINK);

                for (let i: number = 0; i < userDepartmentLinks.length; i++) {
                    // eslint-disable-next-line no-await-in-loop
                    await db.add(IndexedDBStore.USER_DEPARTMENT_LINK, userDepartmentLinks[i]);
                }

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeAllUserDepartmentLinks(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.USER_DEPARTMENT_LINK);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    //categories----------------------------------------------------------------------------------------------------

    public async getAllCategories(): Promise<Category[]> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const categoryObjects: any = await db.getAll(IndexedDBStore.CATEGORY);
                
                if (!Array.isArray(categoryObjects)) {
                    return [];
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const categories: (Category | null)[] = categoryObjects.map((categoryObject: any) => Category.fromIndexedDB(categoryObject));

                return removeNulls(categories);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return [];
        }
    }

    public async replaceAllCategories(categories: Category[]): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.CATEGORY);

                for (let i: number = 0; i < categories.length; i++) {
                    // eslint-disable-next-line no-await-in-loop
                    await db.add(IndexedDBStore.CATEGORY, categories[i]);
                }

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeAllCategories(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.CATEGORY);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    //document tree nodes----------------------------------------------------------------------------------------------------

    public async getAllDocumentTreeNodes(): Promise<DocumentTreeNode[]> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const documentTreeNodeObjects: any = await db.getAll(IndexedDBStore.DOCUMENT_TREE_NODE);
                
                if (!Array.isArray(documentTreeNodeObjects)) {
                    return [];
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const documentTreeNodes: (DocumentTreeNode | null)[] = documentTreeNodeObjects.map((documentTreeNodeObject: any) => DocumentTreeNode.fromIndexedDB(documentTreeNodeObject));

                return removeNulls(documentTreeNodes);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return [];
        }
    }

    public async replaceAllDocumentTreeNodes(documentTreeNodes: DocumentTreeNode[]): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.DOCUMENT_TREE_NODE);

                for (let i: number = 0; i < documentTreeNodes.length; i++) {
                    // eslint-disable-next-line no-await-in-loop
                    await db.add(IndexedDBStore.DOCUMENT_TREE_NODE, documentTreeNodes[i]);
                }

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeAllDocumentTreeNodes(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.DOCUMENT_TREE_NODE);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    //document nodes----------------------------------------------------------------------------------------------------

    public async getAllDocumentNodes(): Promise<DocumentNode[]> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const documentNodeObjects: any = await db.getAll(IndexedDBStore.DOCUMENT_NODE);
                
                if (!Array.isArray(documentNodeObjects)) {
                    return [];
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const documentNodes: (DocumentNode | null)[] = documentNodeObjects.map((documentNodeObject: any) => DocumentNode.fromIndexedDB(documentNodeObject));

                return removeNulls(documentNodes);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return [];
        }
    }

    public async replaceAllDocumentNodes(documentNodes: DocumentNode[]): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.DOCUMENT_NODE);

                for (let i: number = 0; i < documentNodes.length; i++) {
                    // eslint-disable-next-line no-await-in-loop
                    await db.add(IndexedDBStore.DOCUMENT_NODE, documentNodes[i]);
                }

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeAllDocumentNodes(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.DOCUMENT_NODE);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    
    //documents----------------------------------------------------------------------------------------------------

    public async getDocumentBlob(documentId: string): Promise<Blob | null> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
                const object: any = await db.get(IndexedDBStore.DOCUMENT, documentId);

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return safeParseNullDefault<Blob>(object?._blob, new Blob([]));
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return null;
        }
    }

    public async insertDocument(documentId: string, blob: Blob): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.add(IndexedDBStore.DOCUMENT, {
                    _documentId: documentId,
                    _blob: blob,
                });

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async updateDocument(documentId: string, blob: Blob): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.put(IndexedDBStore.DOCUMENT, {
                    _documentId: documentId,
                    _blob: blob,
                });

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeDocument(documentId: string): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.delete(IndexedDBStore.DOCUMENT, documentId);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeAllDocuments(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.DOCUMENT);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    //memotemplates----------------------------------------------------------------------------------------------------

    public async getAllMemoTemplates(): Promise<MemoTemplate[]> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const memoTemplateObjects: any = await db.getAll(IndexedDBStore.MEMO_TEMPLATE);
                
                if (!Array.isArray(memoTemplateObjects)) {
                    return [];
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const memoTemplates: (MemoTemplate | null)[] = memoTemplateObjects.map((memoTemplateObject: any) => MemoTemplate.fromIndexedDB(memoTemplateObject));

                return removeNulls(memoTemplates);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return [];
        }
    }

    public async replaceAllMemoTemplates(memoTemplates: MemoTemplate[]): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.MEMO_TEMPLATE);

                for (let i: number = 0; i < memoTemplates.length; i++) {
                    // eslint-disable-next-line no-await-in-loop
                    await db.add(IndexedDBStore.MEMO_TEMPLATE, memoTemplates[i]);
                }

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeAllMemoTemplates(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.MEMO_TEMPLATE);
                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    //folders----------------------------------------------------------------------------------------------------

    public async getAllFolders(): Promise<Folder[]> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const folderObjects: any = await db.getAll(IndexedDBStore.FOLDER);
                
                if (!Array.isArray(folderObjects)) {
                    return [];
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const folders: (Folder | null)[] = folderObjects.map((folderObject: any) => Folder.fromIndexedDB(folderObject));

                return removeNulls(folders);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return [];
        }
    }

    public async getFolder(folderId: string): Promise<Folder | null> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
                const folderObject: any = await db.get(IndexedDBStore.FOLDER, folderId);

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return Folder.fromIndexedDB(folderObject);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return null;
        }
    }

    public async insertFolder(folder: Folder): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.add(IndexedDBStore.FOLDER, folder);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async updateFolder(folder: Folder): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.put(IndexedDBStore.FOLDER, folder);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeFolder(folderId: string): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.delete(IndexedDBStore.FOLDER, folderId);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeAllFolders(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.FOLDER);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    //observation models----------------------------------------------------------------------------------------------------

    public async getAllObservationModels(): Promise<ObservationModel[]> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const observationModelObjects: any = await db.getAll(IndexedDBStore.OBSERVATION_MODEL);
                
                if (!Array.isArray(observationModelObjects)) {
                    return [];
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const observationModels: (ObservationModel | null)[] = observationModelObjects.map((observationModelObject: any) => ObservationModel.fromIndexedDB(observationModelObject));

                return removeNulls(observationModels);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return [];
        }
    }

    public async getObservationModel(observationId: string): Promise<ObservationModel | null> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
                const observationModelObject: any = await db.get(IndexedDBStore.OBSERVATION_MODEL, observationId);

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return ObservationModel.fromIndexedDB(observationModelObject);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return null;
        }
    }

    public async replaceAllObservationModels(observationModels: ObservationModel[]): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.OBSERVATION_MODEL);

                for (let i: number = 0; i < observationModels.length; i++) {
                    // eslint-disable-next-line no-await-in-loop
                    await db.add(IndexedDBStore.OBSERVATION_MODEL, observationModels[i]);
                }

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeAllObservationModels(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.OBSERVATION_MODEL);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    //filled observations----------------------------------------------------------------------------------------------------

    public async getAllFilledObservations(): Promise<FilledObservation[]> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const filledObservationObjects: any = await db.getAll(IndexedDBStore.FILLED_OBSERVATION);
                
                if (!Array.isArray(filledObservationObjects)) {
                    return [];
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const filledObservations: (FilledObservation | null)[] = filledObservationObjects.map((filledObservationObject: any) => FilledObservation.fromIndexedDB(filledObservationObject));

                return removeNulls(filledObservations);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return [];
        }
    }

    public async getFilledObservation(filledObservationId: string): Promise<FilledObservation | null> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
                const filledObservationObject: any = await db.get(IndexedDBStore.FILLED_OBSERVATION, filledObservationId);

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return FilledObservation.fromIndexedDB(filledObservationObject);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return null;
        }
    }

    public async insertFilledObservation(filledObservation: FilledObservation): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.add(IndexedDBStore.FILLED_OBSERVATION, filledObservation);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async updateFilledObservation(filledObservation: FilledObservation): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.put(IndexedDBStore.FILLED_OBSERVATION, filledObservation);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeFilledObservation(filledObservationId: string): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.delete(IndexedDBStore.FILLED_OBSERVATION, filledObservationId);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeAllFilledObservations(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.FILLED_OBSERVATION);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    //observation chapters----------------------------------------------------------------------------------------------------

    public async getAllObservationChapters(): Promise<ObservationChapter[]> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const observationChapterObjects: any = await db.getAll(IndexedDBStore.OBSERVATION_CHAPTER);
                
                if (!Array.isArray(observationChapterObjects)) {
                    return [];
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const observationChapters: (ObservationChapter | null)[] = observationChapterObjects.map((observationChapterObject: any) => ObservationChapter.fromIndexedDB(observationChapterObject));

                return removeNulls(observationChapters);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return [];
        }
    }

    public async replaceAllObservationChapters(observationChapters: ObservationChapter[]): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.OBSERVATION_CHAPTER);

                for (let i: number = 0; i < observationChapters.length; i++) {
                    // eslint-disable-next-line no-await-in-loop
                    await db.add(IndexedDBStore.OBSERVATION_CHAPTER, observationChapters[i]);
                }

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeAllObservationChapters(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.OBSERVATION_CHAPTER);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    //observation subchapters----------------------------------------------------------------------------------------------------

    public async getAllObservationSubChapters(): Promise<ObservationSubChapter[]> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const observationSubChapterObjects: any = await db.getAll(IndexedDBStore.OBSERVATION_SUBCHAPTER);
                
                if (!Array.isArray(observationSubChapterObjects)) {
                    return [];
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const observationSubChapters: (ObservationSubChapter | null)[] = observationSubChapterObjects.map((observationSubChapterObject: any) => ObservationSubChapter.fromIndexedDB(observationSubChapterObject));

                return removeNulls(observationSubChapters);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return [];
        }
    }

    public async replaceAllObservationSubChapters(observationSubChapters: ObservationSubChapter[]): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.OBSERVATION_SUBCHAPTER);

                for (let i: number = 0; i < observationSubChapters.length; i++) {
                    // eslint-disable-next-line no-await-in-loop
                    await db.add(IndexedDBStore.OBSERVATION_SUBCHAPTER, observationSubChapters[i]);
                }

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeAllObservationSubChapters(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.OBSERVATION_SUBCHAPTER);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    //observation rulesets----------------------------------------------------------------------------------------------------

    public async getAllObservationRuleSets(): Promise<ObservationRuleSet[]> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const observationRuleSetObjects: any = await db.getAll(IndexedDBStore.OBSERVATION_RULESET);
                
                if (!Array.isArray(observationRuleSetObjects)) {
                    return [];
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const observationRuleSets: (ObservationRuleSet | null)[] = observationRuleSetObjects.map((observationRuleSetObject: any) => ObservationRuleSet.fromIndexedDB(observationRuleSetObject));

                return removeNulls(observationRuleSets);
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return [];
        }
    }

    public async replaceAllObservationRuleSets(observationRuleSets: ObservationRuleSet[]): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.OBSERVATION_RULESET);

                for (let i: number = 0; i < observationRuleSets.length; i++) {
                    // eslint-disable-next-line no-await-in-loop
                    await db.add(IndexedDBStore.OBSERVATION_RULESET, observationRuleSets[i]);
                }

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }

    public async removeAllObservationRuleSets(): Promise<boolean> {

        try {
            const db: IDBPDatabase = await this.openDatabase();

            try {
                await db.clear(IndexedDBStore.OBSERVATION_RULESET);

                return true;
            } finally {
                db.close();
            }
        } catch (e: unknown) {
            return false;
        }
    }
}

export default IndexedDBService;